if (!customElements.get('lbr-newsletter-popup')) {
  customElements.define(
    'lbr-newsletter-popup',
    class LbrNewsletterPopup extends HTMLElement {
      constructor() {
        super();
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleSectionSelect = this.handleSectionSelect.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.dismissFloater = this.dismissFloater.bind(this);
        this.handleFloaterReveal = this.handleFloaterReveal.bind(this);
        this.handleFloaterScroll = this.handleFloaterScroll.bind(this);
        this.handleAutoOpenScroll = this.handleAutoOpenScroll.bind(this);
        this.handleNewsletterSubscribed = this.handleNewsletterSubscribed.bind(this);
      }

      connectedCallback() {
        this.dialog = this.querySelector('.lbr-newsletter-dialog');
        this.openButtons = this.querySelectorAll('[data-newsletter-open]');
        this.closeButtons = this.querySelectorAll('[data-newsletter-close]');
        this.testMode = this.dataset.testMode === 'true';
        this.delay = Number.parseInt(this.dataset.delay, 10) || 7;
        this.expiry = Number.parseInt(this.dataset.expiry, 10) || 30;
        this.floaterRevealAfterHero = this.dataset.floaterRevealAfterHero === 'true';
        this.customerSubscribed = this.dataset.customerSubscribed === 'true';
        this.heroRevealThreshold = null;
        this.floaterScrollFrame = null;
        this.isMobile = window.matchMedia('(max-width: 749px)').matches;
        this.cookieName = `lbr-newsletter-popup:${this.dataset.sectionId}`;
        this.subscriptionCookieName = 'lbr-newsletter-subscribed';
        this.floaterDismissalKey = `lbr-newsletter-floater-dismissed:${this.dataset.sectionId}`;
        this.closeTimer = null;
        this.dismissTimer = null;
        this.autoOpenTimer = null;
        this.autoOpenFrame = null;
        this.autoOpenEarliestAt = null;
        this.hasAutoOpenInteraction = false;
        this.previouslyFocusedElement = null;
        this.subscriptionConfirmationShown = false;

        if (!this.dialog) return;

        this.openButtons.forEach((button) => button.addEventListener('click', this.open));
        this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
        this.floater = this.querySelector('.lbr-newsletter-floater');
        this.floaterDismissButton = this.querySelector('[data-newsletter-floater-dismiss]');
        this.floaterDismissButton?.addEventListener('click', this.dismissFloater);
        document.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('lbr:newsletter-subscribed', this.handleNewsletterSubscribed);

        const formStatus = this.querySelector('[data-newsletter-form-status]');
        const searchParams = new URLSearchParams(window.location.search);
        const hasSubmittedNewsletter = searchParams.get('customer_posted') === 'true';
        const hasSuccessfulSubmission =
          formStatus?.dataset.newsletterFormStatus === 'success' || hasSubmittedNewsletter;

        if (hasSuccessfulSubmission) {
          this.markSubscribed();
          this.showSubscriptionConfirmation();
          this.clearSuccessfulSubmissionParameter();
          return;
        }

        if (this.customerSubscribed || this.hasSubscriptionCookie() || this.hasSubscriptionStorage()) {
          this.hideFloater();
          return;
        }

        const floaterDismissedForSession = this.hasFloaterSessionDismissal();
        if (floaterDismissedForSession && this.floater) {
          this.floater.hidden = true;
        }

        if (this.floaterRevealAfterHero && this.floater && !floaterDismissedForSession) {
          this.heroRevealThreshold = this.getHeroRevealThreshold();
          this.handleFloaterReveal();
          window.addEventListener('scroll', this.handleFloaterScroll, { passive: true });
          window.addEventListener('resize', this.handleFloaterReveal, { passive: true });
        }

        if (window.Shopify?.designMode) {
          document.addEventListener('shopify:section:select', this.handleSectionSelect);
          document.addEventListener('shopify:block:select', this.handleBlockSelect);
        }

        if (formStatus) {
          this.open();
          return;
        }

        if (
          window.location.pathname !== '/challenge' &&
          !window.Shopify?.designMode &&
          (this.testMode || !this.getCookie())
        ) {
          this.setupAutoOpen();
        }
      }

      disconnectedCallback() {
        this.teardownAutoOpen();
        window.clearTimeout(this.closeTimer);
        window.clearTimeout(this.dismissTimer);
        document.body.classList.remove('lbr-newsletter-dialog-open');
        this.openButtons?.forEach((button) => button.removeEventListener('click', this.open));
        this.closeButtons?.forEach((button) => button.removeEventListener('click', this.close));
        this.floaterDismissButton?.removeEventListener('click', this.dismissFloater);
        document.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('lbr:newsletter-subscribed', this.handleNewsletterSubscribed);
        document.removeEventListener('shopify:section:select', this.handleSectionSelect);
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        window.removeEventListener('scroll', this.handleFloaterScroll);
        window.removeEventListener('resize', this.handleFloaterReveal);
        if (this.floaterScrollFrame) {
          window.cancelAnimationFrame(this.floaterScrollFrame);
        }
      }

      open(event) {
        event?.preventDefault();
        window.clearTimeout(this.closeTimer);
        this.teardownAutoOpen();
        this.previouslyFocusedElement = document.activeElement;

        document.body.classList.add('lbr-newsletter-dialog-open');
        this.dialog.hidden = false;
        this.openButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
        this.floater?.classList.add('is-dialog-open');

        window.requestAnimationFrame(() => {
          this.dialog.classList.add('is-open');
          if (event) {
            const focusTarget = this.dialog.querySelector(
              'input:not([type="hidden"]), button:not([data-newsletter-close]), a[href]'
            );
            focusTarget?.focus({ preventScroll: true });
          }
        });
      }

      close(event) {
        event?.preventDefault();
        if (!this.dialog.classList.contains('is-open')) return;

        this.dialog.classList.remove('is-open');
        this.openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));

        if (!this.testMode) this.setCookie();

        this.closeTimer = window.setTimeout(() => {
          this.dialog.hidden = true;
          document.body.classList.remove('lbr-newsletter-dialog-open');
          if (this.floater && !this.floater.hidden) {
            this.floater.classList.remove('is-dialog-open');
            this.handleFloaterReveal();
          }
          if (this.previouslyFocusedElement instanceof HTMLElement) {
            this.previouslyFocusedElement.focus({ preventScroll: true });
          }
        }, 500);
      }

      handleKeydown(event) {
        if (event.key === 'Escape' && this.dialog.classList.contains('is-open')) this.close();
      }

      handleNewsletterSubscribed(event) {
        if (event.detail?.form && !this.contains(event.detail.form)) return;

        this.markSubscribed();
        this.showSubscriptionConfirmation();
      }

      showSubscriptionConfirmation() {
        if (this.subscriptionConfirmationShown) return;

        const newsletterForm = this.querySelector('.lbr-newsletter-form');
        const fields = newsletterForm?.querySelector('[data-newsletter-form-fields]');
        const successMessage = newsletterForm?.querySelector(
          '[data-newsletter-success], [data-newsletter-form-status="success"]'
        );

        if (!successMessage) return;

        this.subscriptionConfirmationShown = true;
        if (fields) fields.hidden = true;
        successMessage.hidden = false;
        successMessage.removeAttribute('hidden');
        if (!this.dialog.classList.contains('is-open')) this.open();

        window.requestAnimationFrame(() => {
          this.querySelector('[data-newsletter-close]')?.focus({ preventScroll: true });
        });
      }

      clearSuccessfulSubmissionParameter() {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('customer_posted')) return;

        url.searchParams.delete('customer_posted');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      dismissFloater(event) {
        event?.preventDefault();
        event?.stopPropagation();
        if (!this.floater) return;

        try {
          window.sessionStorage.setItem(this.floaterDismissalKey, 'true');
        } catch (error) {
          // Keep the dismiss action functional when storage is unavailable.
        }

        this.floater.classList.remove('is-visible');
        this.floater.classList.add('is-session-dismissed');
        window.removeEventListener('scroll', this.handleFloaterScroll);
        window.removeEventListener('resize', this.handleFloaterReveal);
        this.dismissTimer = window.setTimeout(() => {
          this.floater.hidden = true;
        }, 400);
      }

      hideFloater() {
        if (!this.floater) return;

        document.documentElement.classList.add('lbr-newsletter-subscribed');
        this.classList.add('is-subscribed');
        this.floater.classList.remove('is-visible');
        this.floater.hidden = true;
      }

      markSubscribed() {
        this.setCookie();
        this.setSubscriptionCookie();
        this.hideFloater();
      }

      handleSectionSelect(event) {
        if (event.detail.sectionId === this.dataset.sectionId) this.open();
      }

      handleBlockSelect(event) {
        if (event.detail.sectionId === this.dataset.sectionId) this.open();
      }

      setupAutoOpen() {
        this.autoOpenEarliestAt = window.performance.now() + this.delay * 1000;
        window.addEventListener('scroll', this.handleAutoOpenScroll, { passive: true });
        window.addEventListener('resize', this.handleAutoOpenScroll, { passive: true });
      }

      teardownAutoOpen() {
        window.clearTimeout(this.autoOpenTimer);
        this.autoOpenTimer = null;
        window.removeEventListener('scroll', this.handleAutoOpenScroll);
        window.removeEventListener('resize', this.handleAutoOpenScroll);

        if (this.autoOpenFrame) {
          window.cancelAnimationFrame(this.autoOpenFrame);
          this.autoOpenFrame = null;
        }
      }

      handleAutoOpenScroll(event) {
        if (event?.type === 'scroll' && event.isTrusted) {
          this.hasAutoOpenInteraction = true;
        }

        if (this.autoOpenFrame) return;

        this.autoOpenFrame = window.requestAnimationFrame(() => {
          this.autoOpenFrame = null;
          this.updateAutoOpen();
        });
      }

      updateAutoOpen() {
        const hasPassedFirstScreen = window.scrollY >= window.innerHeight;
        const canAutoOpen = this.hasAutoOpenInteraction && hasPassedFirstScreen;

        if (!canAutoOpen) {
          window.clearTimeout(this.autoOpenTimer);
          this.autoOpenTimer = null;
          return;
        }

        if (this.autoOpenTimer) return;

        const remainingDelay = Math.max(
          0,
          (this.autoOpenEarliestAt || window.performance.now()) - window.performance.now()
        );

        this.autoOpenTimer = window.setTimeout(() => {
          this.autoOpenTimer = null;

          if (!this.hasAutoOpenInteraction || window.scrollY < window.innerHeight) return;

          this.open();
        }, remainingDelay);
      }

      getHeroRevealThreshold() {
        const mainSection = document.querySelector('main .shopify-section') || document.querySelector('main');
        if (!mainSection) {
          return window.innerHeight;
        }

        return mainSection.getBoundingClientRect().height;
      }

      handleFloaterScroll() {
        if (this.floaterScrollFrame) {
          return;
        }

        this.floaterScrollFrame = window.requestAnimationFrame(() => {
          this.floaterScrollFrame = null;
          this.handleFloaterReveal();
        });
      }

      handleFloaterReveal() {
        if (!this.floater) return;

        if (this.classList.contains('is-subscribed')) {
          this.hideFloater();
          return;
        }

        if (this.floater.hidden || this.floater.classList.contains('is-session-dismissed')) return;

        if (!this.floaterRevealAfterHero) return;

        if (this.heroRevealThreshold === null) {
          this.heroRevealThreshold = this.getHeroRevealThreshold();
        }

        const revealThreshold = this.heroRevealThreshold * 0.5;
        const thresholdOffset = this.isMobile ? 24 : 0;
        const shouldReveal = window.scrollY > (revealThreshold + thresholdOffset);
        const shouldHide = window.scrollY < (revealThreshold - thresholdOffset);

        if (shouldReveal) {
          this.floater.classList.add('is-visible');
          return;
        }

        if (shouldHide) {
          this.floater.classList.remove('is-visible');
          return;
        }

        if (!this.floater.classList.contains('is-visible')) {
          this.floater.classList.remove('is-visible');
        }
      }

      getCookie() {
        return document.cookie
          .split('; ')
          .some((cookie) => cookie.startsWith(`${encodeURIComponent(this.cookieName)}=`));
      }

      hasSubscriptionCookie() {
        return document.cookie
          .split('; ')
          .some((cookie) => cookie.startsWith(`${this.subscriptionCookieName}=`));
      }

      hasFloaterSessionDismissal() {
        try {
          return window.sessionStorage.getItem(this.floaterDismissalKey) === 'true';
        } catch (error) {
          return false;
        }
      }

      hasSubscriptionStorage() {
        try {
          return window.localStorage.getItem(this.subscriptionCookieName) === 'true';
        } catch (error) {
          return false;
        }
      }

      setCookie() {
        const maxAge = this.expiry * 24 * 60 * 60;
        document.cookie = `${encodeURIComponent(this.cookieName)}=true; max-age=${maxAge}; path=/; SameSite=Lax`;
      }

      setSubscriptionCookie() {
        document.cookie = `${this.subscriptionCookieName}=true; max-age=31536000; path=/; SameSite=Lax`;
        try {
          window.localStorage.setItem(this.subscriptionCookieName, 'true');
        } catch (error) {
          // Cookies remain the fallback when local storage is unavailable.
        }
      }
    }
  );
}
